/**
 * Design tokens: the single source of truth for the product's visual language.
 *
 * These are plain frozen data, deliberately platform agnostic, so the web client,
 * the server (for transactional email and SMS styling later) and any future
 * native shell all read the same numbers. The web client's `tokens.css` emits
 * them as CSS custom properties; `tokens.test.js` asserts the two agree so they
 * cannot drift.
 *
 * Two rules the old palette broke, and this file structurally cannot:
 *
 *   1. Names are semantic, not decorative. `danger` says what it is for.
 *      `clay` did not, across roughly 850 call sites.
 *   2. Both themes carry the same key set, and every value is frozen. The old
 *      palette was a mutable module-level object reassigned during render, and a
 *      key present in only one theme would silently keep the other theme's value
 *      because the switch merged rather than replaced.
 *
 * Audit references prevented here:
 *   theme.js #1   (mutable global mutated during render)
 *   theme.js #4   (Object.assign merge could never remove a key)
 *   theme.js #6   (~40 hardcoded rgba values with no elevation or scrim tokens)
 *   theme.js #8   (two accent colours failed WCAG AA as text in light mode)
 *   theme.js #9   (decorative names, and dark mode collapsed two backgrounds)
 *   theme.js #13  (tokens trapped inside the web app tree)
 *   shared.jsx #2 (nine ad hoc z-index values with no scale)
 *   shared.jsx #10 (unnamed layout numbers)
 */

/** Theme names the product supports. `system` is a preference, not a palette. */
export const THEME_NAMES = Object.freeze(['light', 'dark']);

/**
 * Colour tokens, per theme.
 *
 * Naming contract:
 *   bg*       surfaces, in ascending elevation order (page, card, raised)
 *   text*     foreground text
 *   border*   hairlines and outlines
 *   accent    the primary action colour; `onAccent` is what sits on top of it
 *   brand     the EAX maize; a fill and never a text colour in light mode
 *   info / success / warning / danger, each with a `Soft` fill and a `Text`
 *             foreground that is contrast safe on its own `Soft` fill
 *   overlay   modal scrim
 *   shadow*   elevation, as complete box-shadow values
 */
export const COLOR_TOKENS = Object.freeze({
  light: Object.freeze({
    bgPage: '#E8EDF3',
    bgCard: '#F6F8FC',
    bgRaised: '#FFFFFF',

    textPrimary: '#0B1B33',
    textMuted: '#5C6A7A',

    borderDefault: '#C6D1DD',
    // borderStrong marks the boundary of an interactive control, so WCAG 1.4.11
    // requires 3:1 against every surface a control can sit on. The first value
    // chosen here measured 1.72:1 and the token test rejected it.
    borderStrong: '#77879B',

    accent: '#00274C',
    accentHover: '#1A2235',
    onAccent: '#FFFFFF',

    brand: '#FFCB05',
    onBrand: '#00274C',

    info: '#13458B',
    infoSoft: '#D3DEEE',
    infoText: '#1F5066',

    success: '#2E7D52',
    successSoft: '#EAF5EE',
    successText: '#2F5E39',

    warning: '#C68A00',
    warningSoft: '#FFEFBA',
    warningText: '#7A4A14',

    danger: '#C0392B',
    dangerSoft: '#FDEEEC',
    dangerText: '#7A2E1C',

    focusRing: '#D3DEEE',
    overlay: 'rgba(11, 27, 51, 0.55)',

    shadowSm: '0 1px 2px rgba(11, 27, 51, 0.04), 0 4px 16px rgba(11, 27, 51, 0.06)',
    shadowMd: '0 2px 4px rgba(11, 27, 51, 0.05), 0 10px 28px rgba(11, 27, 51, 0.09)',
    shadowAccent: '0 2px 8px rgba(0, 39, 76, 0.18)',
  }),

  dark: Object.freeze({
    // The page stays true black to keep the product's dark identity, but bgCard
    // is a real elevation step above it. The old dark palette set both to
    // #000000, so a card had zero separation from the page and depended entirely
    // on its hairline border.
    bgPage: '#000000',
    bgCard: '#101317',
    bgRaised: '#191D23',

    textPrimary: '#F2F5F9',
    textMuted: '#98A6B5',

    borderDefault: '#2A2E36',
    // See the light note. The first value here measured 2.01:1.
    borderStrong: '#626C7A',

    accent: '#123A63',
    accentHover: '#194B7D',
    onAccent: '#FFFFFF',

    brand: '#FFCB05',
    onBrand: '#00274C',

    info: '#5B9BEA',
    infoSoft: '#132A47',
    infoText: '#9CC5EA',

    success: '#4CAF7D',
    successSoft: '#123321',
    successText: '#8FDCAE',

    warning: '#E5A93D',
    warningSoft: '#3A2C10',
    warningText: '#F0C674',

    danger: '#E5645A',
    dangerSoft: '#3A1613',
    dangerText: '#F0A79E',

    focusRing: '#132A47',
    overlay: 'rgba(0, 0, 0, 0.66)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.45)',
    shadowMd: '0 2px 4px rgba(0, 0, 0, 0.55), 0 10px 28px rgba(0, 0, 0, 0.5)',
    shadowAccent: '0 2px 8px rgba(0, 0, 0, 0.55)',
  }),
});

/** Spacing scale, in CSS pixels. */
export const SPACE_TOKENS = Object.freeze({
  '2xs': 4,
  xs: 6,
  sm: 8,
  md: 10,
  lg: 14,
  xl: 16,
  '2xl': 22,
  '3xl': 30,
});

/** Corner radii, in CSS pixels. `pill` is a large value, not a percentage. */
export const RADIUS_TOKENS = Object.freeze({
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  pill: 999,
});

/** Typography. Two families: one for body, one for display and figures. */
export const TYPOGRAPHY_TOKENS = Object.freeze({
  fontBody: "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontDisplay: "'Space Grotesk', 'Inter', system-ui, sans-serif",
  sizeXs: 11,
  sizeSm: 12.5,
  sizeMd: 13,
  sizeLg: 15,
  sizeXl: 24,
  weightRegular: 400,
  weightMedium: 500,
  weightSemibold: 600,
  weightBold: 700,
  weightHeavy: 800,
});

/**
 * Layering scale. Components reference these names and never a numeric literal.
 * The old app had 30, 50, 200 (four times), 1000 (three times), 1100, 1200, 1201
 * and 2000 chosen by hand, so an overlay opened from inside a modal rendered
 * behind it.
 */
export const LAYER_TOKENS = Object.freeze({
  base: 0,
  sticky: 100,
  overlay: 200,
  modal: 300,
  popover: 400,
  toast: 500,
});

/** Motion. Every animated surface uses a named duration and a named easing. */
export const MOTION_TOKENS = Object.freeze({
  durationInstant: 120,
  durationFast: 180,
  durationBase: 260,
  easeStandard: 'cubic-bezier(0.2, 0, 0.2, 1)',
  easeEmphasised: 'cubic-bezier(0.16, 1, 0.3, 1)',
});

/**
 * Named breakpoints, in CSS pixels.
 *
 * CSS media queries cannot read custom properties, so the stylesheets repeat
 * these numbers as literals. `tokens.test.js` asserts the stylesheet and this
 * object agree, which is what stops them drifting.
 */
export const BREAKPOINT_TOKENS = Object.freeze({
  phoneMax: 480,
  tabletMin: 561,
  desktopMin: 901,
});

/** Layout constants that are product decisions rather than free choices. */
export const LAYOUT_TOKENS = Object.freeze({
  /** Widest the page content ever gets, in CSS pixels. */
  contentMaxWidth: 1080,
  /** WCAG 2.2 AA minimum interactive target, in CSS pixels. */
  minTouchTarget: 44,
  /** Header and nav control diameter, in CSS pixels. */
  headerControlSize: 40,
});
